import { Briefcase, CreditCard, MapPin, UserCircle } from 'lucide-react'
import { GESTOR_SETOR_LABELS } from '@/config/gestorAccess'
import { GestorEquipeDadosCompleteness } from '@/components/Gestores/GestorEquipeDadosCompleteness'
import {
  formatCep,
  formatCnpj,
  formatContaBanco,
  formatCpf,
  formatEndereco,
  formatPhone,
} from '@/components/PjAgrada/pjAgradaFormat'
import type { GestorEquipeDetail } from '@/services/gestoresService'
import { isPlaceholderPjPhone } from '@/utils/pjAgradaCompleteness'
import { cn } from '@/utils/cn'

const STATUS_LABELS: Record<string, string> = {
  ATIVO: 'Ativo',
  INATIVO: 'Inativo',
  NAO_PREENCHIDO: 'Cadastro pendente',
  NAO_CONCLUIDO: 'Cadastro incompleto',
}

const TIPO_CONTA_LABELS: Record<string, string> = {
  corrente: 'Corrente',
  poupanca: 'Poupança',
}

function formatMoney(v: number | null) {
  if (v == null) return '—'
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDate(value: string | null | undefined, style: 'short' | 'long' = 'short') {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('pt-BR', style === 'long' ? { month: 'long', year: 'numeric' } : undefined)
}

function Field({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  const empty = value === '—'
  return (
    <div className='min-w-0'>
      <p className='text-[11px] font-medium uppercase tracking-wide text-text-muted'>{label}</p>
      <p
        className={cn(
          'mt-0.5 text-sm font-medium break-words',
          empty || warn ? 'text-amber-500/90' : 'text-text-primary',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <section className='space-y-3'>
      <h3 className='flex items-center gap-2 text-sm font-semibold text-text-primary'>
        {icon}
        {title}
      </h3>
      <div className='grid gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 sm:grid-cols-2'>
        {children}
      </div>
    </section>
  )
}

type Props = {
  detail: GestorEquipeDetail
}

export function GestorEquipeDadosPanel({ detail }: Props) {
  const telefone = isPlaceholderPjPhone(detail.telefone)
    ? '— (pendente)'
    : formatPhone(detail.telefone) || detail.telefone
  const enderecoLinha = formatEndereco(detail)
  const mesesParceria = detail.feriasResumo?.mesesParceria

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-start justify-between gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-4'>
        <div>
          <p className='text-sm font-semibold text-text-primary'>Vínculo com a Agrada</p>
          <p className='mt-1 text-sm text-text-muted'>
            Colaborador PJ desde{' '}
            <span className='font-medium text-text-primary'>
              {formatDate(detail.dataInicioContrato ?? detail.createdAt, 'long')}
            </span>
            {mesesParceria != null ? ` · ${mesesParceria} mês(es) de parceria` : ''}
          </p>
        </div>
        <span
          className={cn(
            'rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset',
            detail.status === 'ATIVO'
              ? 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30'
              : 'bg-amber-500/15 text-amber-400 ring-amber-500/30',
          )}
        >
          {STATUS_LABELS[detail.status] ?? detail.status}
        </span>
      </div>

      <GestorEquipeDadosCompleteness detail={detail} />

      <Section icon={<UserCircle size={16} className='text-accent' />} title='Contato e identificação'>
        <Field label='E-mail' value={detail.email?.trim() || '—'} />
        <Field label='Telefone / WhatsApp' value={telefone} warn={isPlaceholderPjPhone(detail.telefone)} />
        <Field label='CPF' value={detail.cpf ? formatCpf(detail.cpf) : '—'} />
        <Field label='CNPJ' value={detail.cnpj ? formatCnpj(detail.cnpj) : '—'} />
        <Field label='Razão social' value={detail.razaoSocial?.trim() || '—'} />
        <Field
          label='Portal Conecta PJ'
          value={detail.portalUserId ? 'Usuário vinculado' : 'Sem acesso ao portal'}
          warn={!detail.portalUserId}
        />
      </Section>

      <Section icon={<Briefcase size={16} className='text-accent' />} title='Atuação e contrato'>
        <Field
          label='Setor'
          value={
            detail.setor
              ? (GESTOR_SETOR_LABELS[detail.setor] ?? detail.setor)
              : '—'
          }
        />
        <Field label='Área de atuação' value={detail.areaAtuacao?.trim() || '—'} />
        <Field label='Início do contrato' value={formatDate(detail.dataInicioContrato)} />
        <Field label='Remuneração mensal' value={formatMoney(detail.remuneracaoMensal)} />
        <Field label='Cadastro no CRM' value={formatDate(detail.createdAt)} />
        <Field label='Última atualização' value={formatDate(detail.updatedAt)} />
      </Section>

      <Section icon={<MapPin size={16} className='text-accent' />} title='Endereço'>
        <Field label='CEP' value={detail.cep ? formatCep(detail.cep) : '—'} />
        <Field label='Logradouro' value={detail.logradouro?.trim() || '—'} />
        <Field label='Número' value={detail.numero?.trim() || '—'} />
        <Field label='Complemento' value={detail.complemento?.trim() || '—'} />
        <Field label='Bairro' value={detail.bairro?.trim() || '—'} />
        <Field label='Cidade / UF' value={[detail.cidade, detail.uf].filter(Boolean).join(' - ') || '—'} />
        {enderecoLinha !== '—' && (
          <div className='sm:col-span-2'>
            <Field label='Endereço completo' value={enderecoLinha} />
          </div>
        )}
      </Section>

      <Section icon={<CreditCard size={16} className='text-accent' />} title='Dados bancários e PIX'>
        <Field label='Banco' value={detail.bancoNome?.trim() || '—'} />
        <Field label='Agência' value={detail.bancoAgencia?.trim() || '—'} />
        <Field label='Conta' value={detail.bancoConta?.trim() || '—'} />
        <Field
          label='Tipo de conta'
          value={
            detail.bancoTipoConta
              ? (TIPO_CONTA_LABELS[detail.bancoTipoConta] ?? detail.bancoTipoConta)
              : '—'
          }
        />
        <Field label='Chave PIX' value={detail.pixChave?.trim() || '—'} />
        <Field label='Resumo bancário' value={formatContaBanco(detail)} />
      </Section>

      {detail.observacoes?.trim() && (
        <section className='space-y-2'>
          <h3 className='text-sm font-semibold text-text-primary'>Observações internas</h3>
          <p className='whitespace-pre-wrap rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 text-sm text-text-muted'>
            {detail.observacoes}
          </p>
        </section>
      )}
    </div>
  )
}
